/**
 * The serve screen's on-screen keyboard (tablet mode, PROD-1278 feedback →
 * PROD-1428 layout): its key layout and what a press does to the search
 * query. Pure: the component renders the rows, the page dispatches the
 * result through the same `SET_QUERY` the input itself uses.
 *
 * Layout (PROD-1428 item 12, Matt): the PHONE / iPad number pad — `1 2 3` /
 * `4 5 6` / `7 8 9` / blank `0` ⌫ — because student IDs are what a line
 * types most and the calculator order (7-8-9 on top) and the ambiguous "C"
 * key both confused staff; then three letter rows for names with a second
 * backspace at the end of the `z–m` row, as on the iPad keyboard; then a
 * space bar. Letters are lowercase only: the search is case-insensitive
 * (`matchesAnyField` lowercases both sides), so a shift key would be noise.
 * There is no Enter: a unique match opens its row on its own
 * (`resolveSingleMatch`), and a barcode scanner or hardware keyboard still
 * types into the focused input and submits its form. Clearing the whole
 * query is the readout's ✕ (the `clear` kind), not a pad key.
 *
 * Presses edit the END of the query. The worker has no arrow keys, and the
 * caret follows the value React writes back into the input.
 */

export type VirtualKey =
  | { kind: 'char'; char: string }
  | { kind: 'space' }
  | { kind: 'backspace' }
  /** The readout's ✕: the whole query goes. */
  | { kind: 'clear' }
  /** Done: the count boxes' pad closes on it (tapping outside also closes it, but nobody knows that). */
  | { kind: 'confirm' }

/** The three key shapes: the pad's big keys, the letter keys, the lone space bar. */
export type VirtualKeyboardRowVariant = 'numbers' | 'letters' | 'space'

export interface VirtualKeyboardRow {
  id: string
  variant: VirtualKeyboardRowVariant
  /** Keys share the row width equally instead of sitting at a fixed width, centered. */
  fill: boolean
  /** `null` is an empty cell (the pad's bottom-left, under the 1). */
  keys: readonly (VirtualKey | null)[]
}

function chars(letters: string): VirtualKey[] {
  return Array.from(letters, (char) => ({ kind: 'char', char }))
}

const BACKSPACE: VirtualKey = { kind: 'backspace' }

/** The number pad: four rows of three cells, phone order. */
export const VIRTUAL_NUMBER_ROWS: readonly VirtualKeyboardRow[] = [
  { id: 'numbers-123', variant: 'numbers', fill: true, keys: chars('123') },
  { id: 'numbers-456', variant: 'numbers', fill: true, keys: chars('456') },
  { id: 'numbers-789', variant: 'numbers', fill: true, keys: chars('789') },
  { id: 'numbers-0', variant: 'numbers', fill: true, keys: [null, ...chars('0'), BACKSPACE] },
]

/** The letter rows and the space bar. */
export const VIRTUAL_LETTER_ROWS: readonly VirtualKeyboardRow[] = [
  { id: 'letters-top', variant: 'letters', fill: true, keys: chars('qwertyuiop') },
  { id: 'letters-home', variant: 'letters', fill: false, keys: chars('asdfghjkl') },
  { id: 'letters-bottom', variant: 'letters', fill: false, keys: [...chars('zxcvbnm'), BACKSPACE] },
  { id: 'space', variant: 'space', fill: false, keys: [{ kind: 'space' }] },
]

/** Every row, pad first, for callers that walk the whole keyboard. */
export const VIRTUAL_KEYBOARD_ROWS: readonly VirtualKeyboardRow[] = [...VIRTUAL_NUMBER_ROWS, ...VIRTUAL_LETTER_ROWS]

/**
 * Where the keyboard sits (PROD-1428 item 14): down the RIGHT column on a
 * landscape screen (pad above the letters), or across the BOTTOM of a
 * portrait tablet like a computer keyboard with its keypad — letters on the
 * left, the number pad on the right — with the list scrolling above it.
 * Unknown orientation reads as landscape (the column), the layout tablet
 * mode has always had.
 */
export type VirtualKeyboardLayout = 'column' | 'bottom'

export function virtualKeyboardLayout(env: { landscape: boolean | null }): VirtualKeyboardLayout {
  return env.landscape === false ? 'bottom' : 'column'
}

/** A stable identity per key, for React keys and test hooks. */
export function virtualKeyId(key: VirtualKey): string {
  return key.kind === 'char' ? `char-${key.char}` : key.kind
}

/** The accessible name. A character key reads as its character. */
export function virtualKeyLabel(key: VirtualKey): string {
  switch (key.kind) {
    case 'char':
      return key.char
    case 'space':
      return 'Space'
    case 'backspace':
      return 'Backspace'
    case 'clear':
      return 'Clear search'
    case 'confirm':
      return 'Done'
  }
}

/** The query after one press. */
export function applyVirtualKey(query: string, key: VirtualKey): string {
  switch (key.kind) {
    case 'char':
      return query + key.char
    case 'space':
      return `${query} `
    case 'backspace':
      // One user-visible character, never half of a surrogate pair.
      return Array.from(query).slice(0, -1).join('')
    case 'clear':
      return ''
    case 'confirm':
      // Confirming changes nothing about the text; the box closes its pad.
      return query
  }
}
